import { prefersReducedMotion, readDurationToken } from "@theme/utilities";

const BUTTON_SELECTOR = ".button";
const EXCLUDED_BUTTON_SELECTOR =
  ".button-secondary, .button-unstyled, .menu-drawer__close-button, .add-to-cart-button, .tea-card__add";
const PRESS_FEEDBACK_ATTRIBUTE = "buttonPressFeedback";
const PRESS_FEEDBACK_HOLD_FALLBACK_MS = 110;
const PAINT_FALLBACK_MS = 50;
const STRING_TRUE = "true";
const finePointer = matchMedia("(any-pointer: fine)");

/**
 * @typedef {object} PressFeedbackState
 * @property {boolean} complete - Whether the minimum painted press has finished.
 * @property {boolean} navigationScheduled - Whether this pulse already owns a navigation.
 * @property {Promise<void>} finished - Resolves after the minimum painted press.
 */

/** @type {WeakMap<HTMLElement, PressFeedbackState>} */
const feedbackByButton = new WeakMap();
/** @type {Map<number, HTMLElement>} */
const buttonByPointer = new Map();

/** @returns {Promise<void>} */
const waitForPaint = () =>
  new Promise(resolve => {
    let settled = false;
    let fallbackTimer = 0;
    const settle = () => {
      if (settled) {
        return;
      }

      settled = true;
      globalThis.clearTimeout(fallbackTimer);
      resolve();
    };
    fallbackTimer = globalThis.setTimeout(settle, PAINT_FALLBACK_MS);
    requestAnimationFrame(settle);
  });

/**
 * Finds an enabled standard filled button for delegated feedback.
 * Transactional cart controls retain their ProductForm-owned sequence.
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
const getFeedbackButton = target => {
  if (!(target instanceof Element)) {
    return null;
  }

  const button = target.closest(BUTTON_SELECTOR);
  if (!(button instanceof HTMLElement) || button.matches(EXCLUDED_BUTTON_SELECTOR)) {
    return null;
  }

  if (button.matches(":disabled") || button.getAttribute("aria-disabled") === STRING_TRUE) {
    return null;
  }

  return button;
};

/** @returns {boolean} */
const feedbackIsEnabled = () => finePointer.matches && !prefersReducedMotion();

/**
 * Holds the CSS press state through its first painted frame and full press-in.
 * The triggering action continues concurrently unless it is a plain link
 * navigation, which is coordinated separately below.
 * @param {HTMLElement} button
 * @param {PressFeedbackState} state
 */
const playPressFeedback = async (button, state) => {
  button.dataset[PRESS_FEEDBACK_ATTRIBUTE] = STRING_TRUE;
  await waitForPaint();
  await new Promise(resolve =>
    globalThis.setTimeout(
      resolve,
      readDurationToken("--button-press-feedback-hold-duration", PRESS_FEEDBACK_HOLD_FALLBACK_MS, button),
    ),
  );
  delete button.dataset[PRESS_FEEDBACK_ATTRIBUTE];
  state.complete = true;
};

/**
 * @param {HTMLElement} button
 * @returns {PressFeedbackState}
 */
const startPressFeedback = button => {
  const current = feedbackByButton.get(button);
  if (current) {
    return current;
  }

  /** @type {PressFeedbackState} */
  const state = { complete: false, navigationScheduled: false, finished: Promise.resolve() };
  state.finished = playPressFeedback(button, state);
  feedbackByButton.set(button, state);
  return state;
};

/**
 * @param {HTMLElement} button
 * @param {PressFeedbackState} state
 */
const forgetFeedback = async (button, state) => {
  await state.finished;
  if (feedbackByButton.get(button) === state) {
    feedbackByButton.delete(button);
  }
};

/**
 * @param {MouseEvent} event
 * @returns {boolean}
 */
const isPlainPrimaryActivation = event => {
  if (event.defaultPrevented || event.button !== 0) {
    return false;
  }

  if (event.metaKey || event.ctrlKey) {
    return false;
  }

  return !event.shiftKey && !event.altKey;
};

/**
 * @param {HTMLElement} button
 * @returns {button is HTMLAnchorElement}
 */
const isDelayableAnchor = button => {
  if (!(button instanceof HTMLAnchorElement) || button.hasAttribute("download")) {
    return false;
  }

  if (button.target && button.target.toLowerCase() !== "_self") {
    return false;
  }

  if (button.dataset.buttonPressNavigation === "immediate" || button.dataset.vtMediaTarget !== undefined) {
    return false;
  }

  return !button.querySelector("[data-vt-product-media]");
};

/**
 * Returns a destination only when replacing native navigation for a moment is
 * safe. Modified clicks, downloads, other browsing contexts, external URLs,
 * and product-image view-transition links remain entirely browser-owned.
 * @param {MouseEvent} event
 * @param {HTMLElement} button
 * @returns {URL | null}
 */
const getDelayableDestination = (event, button) => {
  if (!isPlainPrimaryActivation(event) || !isDelayableAnchor(button)) {
    return null;
  }

  let destination;
  try {
    destination = new URL(button.href, globalThis.location.href);
  } catch {
    return null;
  }

  if (destination.origin !== globalThis.location.origin || !/^https?:$/.test(destination.protocol)) {
    return null;
  }

  return destination;
};

/**
 * @param {URL} destination
 * @param {PressFeedbackState} state
 */
const navigateAfterFeedback = async (destination, state) => {
  await state.finished;
  globalThis.location.assign(destination.href);
};

/** @param {PointerEvent} event */
const handlePointerDown = event => {
  if (
    !event.isTrusted ||
    event.button !== 0 ||
    !event.isPrimary ||
    event.pointerType === "touch" ||
    !feedbackIsEnabled()
  ) {
    return;
  }

  const button = getFeedbackButton(event.target);
  if (!button) {
    return;
  }

  startPressFeedback(button);
  buttonByPointer.set(event.pointerId, button);
};

/** @param {PointerEvent} event */
const handlePointerEnd = event => {
  const button = buttonByPointer.get(event.pointerId);
  buttonByPointer.delete(event.pointerId);
  if (!button) {
    return;
  }

  const state = feedbackByButton.get(button);
  if (!state) {
    return;
  }

  // A trusted click is dispatched immediately after pointerup. Defer cleanup
  // one task so that click can reuse even an already-completed long press.
  globalThis.setTimeout(() => forgetFeedback(button, state), 0);
};

/** @param {MouseEvent} event */
const handleClick = event => {
  if (!event.isTrusted || (event instanceof PointerEvent && event.pointerType === "touch") || !feedbackIsEnabled()) {
    return;
  }

  const button = getFeedbackButton(event.target);
  if (!button) {
    return;
  }

  const state = startPressFeedback(button);
  forgetFeedback(button, state);

  const destination = getDelayableDestination(event, button);
  if (!destination || state.complete) {
    return;
  }

  event.preventDefault();
  if (state.navigationScheduled) {
    return;
  }

  state.navigationScheduled = true;
  navigateAfterFeedback(destination, state);
};

document.addEventListener("pointerdown", handlePointerDown, { capture: true, passive: true });
document.addEventListener("pointerup", handlePointerEnd, { capture: true, passive: true });
document.addEventListener("pointercancel", handlePointerEnd, { capture: true, passive: true });
document.addEventListener("click", handleClick);
